/**
 * Chat API
 *
 * Network-only functions for chat-related API calls.
 * NO Redux imports allowed in this file.
 */
import { CHAT_ENDPOINTS } from '@/api/endpoints';
import {
  type AssistantMessage,
  type UserMessage,
  type MessageType,
  type ActiveTemplate,
  type TemplateData,
  type ChatStreamRequest,
  type ChatStreamEvent,
} from '@contracts';

import {
  isThinkingEvent,
  isFinalResponseEvent,
  isStreamEndEvent,
  type ChatStreamCallbacks,
} from '@/features/chat/utils';

import { logger } from '@/shared/utils/logger';
import { getUUID } from '@/shared/utils/appCookies';
import { buildHeaders, apiFetch } from '@/api/utils';

// ============================================
// Types
// ============================================

export interface ChatSummaryResponse {
  data: {
    messages: MessageType[];
    hasMore: boolean;
    nextCursor: string | null;
    templateDetailsUrl: string | null;
  };
}

export interface ChatTemplateDetailsResponse {
  data: {
    activeTemplate: ActiveTemplate;
    templateData: TemplateData;
  };
}

export interface FetchChatHistoryParams {
  userId: string;
  sessionId: string;
  cursor?: string;
  limit?: number;
}

const DEFAULT_CHAT_HISTORY_LIMIT = 10;

/**
 * SSE Event parser for Server-Sent Events
 */
class SSEParser {
  private buffer = '';

  /**
   * Parse incoming SSE data chunk and extract complete events
   */
  parseChunk(chunk: string): Array<AssistantMessage> {
    this.buffer += chunk;
    const events: Array<AssistantMessage> = [];

    // Split by double newline (SSE event separator)
    const lines = this.buffer.split('\n');
    this.buffer = lines.pop() ?? '';

    let eventData = '';

    for (const line of lines) {
      // Skip empty lines and comments
      if (line.trim() === '' || line.startsWith(':')) {
        continue;
      }

      // Parse SSE field - handle both "data: " (with space) and "data:" (without space)
      // SSE spec says the space after colon is optional
      if (line.startsWith('data:')) {
        // Remove "data:" prefix, then trim leading space if present
        eventData = line.slice(5);
        if (eventData.startsWith(' ')) {
          eventData = eventData.slice(1);
        }

        try {
          const parsed = JSON.parse(eventData) as AssistantMessage;
          events.push(parsed);
          eventData = '';
        } catch (error) {
          logger.warn('Failed to parse SSE event data:', eventData, error);
        }
      }
    }

    return events;
  }

  /**
   * Reset parser state
   */
  reset(): void {
    this.buffer = '';
  }
}

/**
 * Send chat message and stream assistant responses via SSE
 *
 * @param message - User message to send
 * @param sessionId - Session identifier
 * @param onMessage - Callback for each streamed message chunk
 * @param onComplete - Callback when stream completes
 * @param onError - Callback for errors
 * @returns Abort function to cancel the stream
 */
export async function sendChatSSE(
  message: UserMessage,
  sessionId: string,
  onMessage: (message: AssistantMessage) => void,
  onComplete: () => void,
  onError: (error: Error) => void,
): Promise<() => void> {
  const query = message.content;
  if (!query) {
    onError(new Error('Content type not supported yet'));
    return () => {};
  }

  const abortController = new AbortController();
  const parser = new SSEParser();

  try {
    const response = await fetch(CHAT_ENDPOINTS.SEND_MESSAGE, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        query,
        userId: getUUID(),
        sessionId,
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    // Stream response body
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            onComplete();
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          const events = parser.parseChunk(chunk);

          for (const event of events) {
            onMessage(event);
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          logger.warn('Stream aborted by user');
          return;
        }

        onError(error instanceof Error ? error : new Error(String(error)));
      } finally {
        parser.reset();
        reader.releaseLock();
      }
    };

    // Start processing stream
    void processStream();

    // Return abort function
    return () => {
      abortController.abort();
    };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Request aborted before stream started');
      return () => {};
    }

    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {};
  }
}

// ============================================
// Chat Stream API (New SSE with event types)
// ============================================

/**
 * SSE Event parser for the new chat stream API.
 * Handles THINKING, FINAL_RESPONSE, and STREAM_END events.
 */
class ChatStreamSSEParser {
  private buffer = '';

  /**
   * Parse incoming SSE data chunk and extract complete events
   */
  parseChunk(chunk: string): Array<ChatStreamEvent> {
    this.buffer += chunk;
    const events: Array<ChatStreamEvent> = [];

    // Split by newline
    const lines = this.buffer.split('\n');
    this.buffer = lines.pop() ?? '';

    let eventData = '';

    for (const line of lines) {
      // Skip empty lines and comments
      if (line.trim() === '' || line.startsWith(':')) {
        continue;
      }

      // Parse SSE data field - handle both "data: " (with space) and "data:" (without space)
      if (line.startsWith('data:')) {
        eventData = line.slice(5);
        if (eventData.startsWith(' ')) {
          eventData = eventData.slice(1);
        }

        try {
          const parsed = JSON.parse(eventData) as ChatStreamEvent;
          events.push(parsed);
          eventData = '';
        } catch (error) {
          logger.warn('Failed to parse chat stream SSE event data:', eventData, error);
        }
      }
    }

    return events;
  }

  /**
   * Reset parser state
   */
  reset(): void {
    this.buffer = '';
  }
}

/**
 * Result of starting a chat stream.
 * Contains both the abort function (available immediately) and a promise for completion tracking.
 */
export interface ChatStreamHandle {
  /** Abort function to cancel the stream - available immediately, works in pending state */
  abort: () => void;
  /** Promise that resolves when stream completes or rejects on error */
  promise: Promise<void>;
}

/**
 * Send chat message via the new streaming API with event types.
 * Handles THINKING, FINAL_RESPONSE, and STREAM_END events.
 *
 * IMPORTANT: This function returns SYNCHRONOUSLY with an abort function that works
 * even while the request is still pending. The promise can be awaited for completion tracking.
 *
 * @param request - Chat stream request payload
 * @param callbacks - Callbacks for handling different event types
 * @returns ChatStreamHandle with abort function (immediate) and promise (for completion)
 */
export function sendChatStream(
  request: ChatStreamRequest,
  callbacks: ChatStreamCallbacks,
): ChatStreamHandle {
  const { onThinking, onFinalResponse, onStreamEnd, onError } = callbacks;

  const abortController = new AbortController();
  const parser = new ChatStreamSSEParser();
  let wasAborted = false;

  // Create the promise for stream completion tracking
  const promise = new Promise<void>((resolve, reject) => {
    const startStream = async () => {
      try {
        const headers = buildHeaders();
        const response = await fetch(CHAT_ENDPOINTS.SEND_MESSAGE_STREAM, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        // Stream response body
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const events = parser.parseChunk(chunk);

            for (const event of events) {
              if (isThinkingEvent(event)) {
                logger.debug('CHAT_STREAM_THINKING', event.thinkingMessage);
                onThinking?.(event);
              } else if (isFinalResponseEvent(event)) {
                logger.debug('CHAT_STREAM_FINAL_RESPONSE', event);
                onFinalResponse(event);
              } else if (isStreamEndEvent(event)) {
                logger.debug('CHAT_STREAM_END', event);
                onStreamEnd?.(event);
              }
            }
          }
          resolve();
        } finally {
          parser.reset();
          reader.releaseLock();
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          logger.warn('Chat stream aborted by user');
          // Resolve (not reject) on abort - it's a user-initiated cancellation
          resolve();
          return;
        }

        onError(error instanceof Error ? error : new Error(String(error)));
        reject(error);
      }
    };

    // Start the stream processing
    void startStream();
  });

  // Return handle with abort function IMMEDIATELY (before fetch completes)
  return {
    abort: () => {
      if (!wasAborted) {
        wasAborted = true;
        abortController.abort();
      }
    },
    promise,
  };
}

// ============================================
// Chat Summary API
// ============================================

/**
 * Fetch chat summary for a session (messages + pagination).
 * Returns messages for the left panel and a templateDetailsUrl
 * for fetching right-panel template data separately.
 *
 * Uses apiFetch for automatic JWT refresh on 401 and session re-init on 403.
 *
 * @param params - Fetch parameters including userId, sessionId, cursor, and limit
 * @returns Chat summary with messages, pagination info, and templateDetailsUrl
 */
export async function fetchChatSummary(
  params: FetchChatHistoryParams,
): Promise<ChatSummaryResponse> {
  const { userId, sessionId, cursor, limit = DEFAULT_CHAT_HISTORY_LIMIT } = params;

  const queryParams: Record<string, string> = {
    userId,
    sessionId,
    limit: String(limit),
  };

  if (cursor) {
    queryParams['cursor'] = cursor;
  }

  const responseData = await apiFetch<ChatSummaryResponse>(CHAT_ENDPOINTS.CHAT_SUMMARY, {
    method: 'GET',
    queryParams,
    skipCommonParams: true,
  });

  logger.debug('Chat summary response:', responseData);

  return {
    data: {
      messages: responseData.data.messages,
      hasMore: responseData.data.hasMore ?? false,
      nextCursor: responseData.data.nextCursor ?? null,
      templateDetailsUrl: responseData.data.templateDetailsUrl ?? null,
    },
  };
}

// ============================================
// Chat Template Details API
// ============================================

/**
 * Fetch template details for the right panel (activeTemplate + templateData).
 * Called after fetchChatSummary to load the right-panel content asynchronously.
 *
 * Uses apiFetch for automatic JWT refresh on 401 and session re-init on 403.
 *
 * @param params - userId and sessionId for the request
 * @returns Template details with activeTemplate and templateData
 */
export async function fetchTemplateDetails(params: {
  userId: string;
  sessionId: string;
}): Promise<ChatTemplateDetailsResponse> {
  const { userId, sessionId } = params;

  const responseData = await apiFetch<ChatTemplateDetailsResponse>(
    CHAT_ENDPOINTS.CHAT_TEMPLATE_DETAILS,
    {
      method: 'GET',
      queryParams: { sessionId, userId },
      skipCommonParams: true,
    },
  );

  logger.debug('Template details response:', responseData);

  return {
    data: {
      activeTemplate: responseData.data.activeTemplate,
      templateData: responseData.data.templateData,
    },
  };
}
